import { modelOptions, post, prop } from "@typegoose/typegoose";
import { z } from "zod";
import { E164PhoneNumber } from "../../validators/index.js";
import { getZodSchemaFromClass, validate } from "../../zod-decorator/index.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../schemaBase.js";
import type {
    MessagingChannelAddressFieldsWithDefaults,
    MessagingChannelAddressSchemaTypes,
} from "../types/messagingChannelAddress.d.js";
import { asZodObject, type DtoValidatorTypes } from "../validatableClass.js";

const SCHEMA_KEY = "MessagingChannelAddress" as const;

/**
 * A channel is how an endpoint is addressed, not how a message reaches it. RCS is deliberately absent:
 * Twilio's messaging service picks RCS or SMS per message and falls back between them, so it is a
 * transport detail of an SMS send.
 */
export enum MessagingChannel {
    SMS = "SMS",
    /** Not deliverable. Declared so tests have a second channel to tell endpoint identity apart with. */
    WHATSAPP = "WHATSAPP",
}

/**
 * Joins a channel to one of its values as a key that survives our transports: BullMQ rejects a colon in a custom
 * job id, and Redis reads braces as a cluster hash tag. Percent-encoding is injective, so no two values collapse
 * onto one key — which matters because these keys are how duplicates are suppressed.
 */
export function channelScopedKey(channel: MessagingChannel, value: string): string {
    return `${channel}-${encodeURIComponent(value)}`;
}

/**
 * The channels wired end to end. Requests and job payloads are held to this list — see
 * `supportedChannelAddress` — while stored rows and keys stay free to carry the rest of the enum.
 */
export const SUPPORTED_MESSAGING_CHANNELS = [MessagingChannel.SMS] as const;

/**
 * The two fields that identify an endpoint, and all that survives a JSON round-trip through Redis —
 * so job payloads and cache keys are typed on this rather than on the class.
 */
export type ChannelAddressKey = Pick<MessagingChannelAddress, "channel" | "address">;

/**
 * One endpoint we can exchange messages with; an address is only routable with a channel, so they travel together.
 * A value, not a role — the field naming it says which end it is: `sender` inbound, `recipient` outbound.
 */
@modelOptions({ schemaOptions: { ...getDefaultSchemaOptions(SCHEMA_KEY) } })
@post<SchemaBase<MessagingChannelAddress>>(...addSchemaKeyHook(SCHEMA_KEY))
export class MessagingChannelAddress extends SchemaBase<
    MessagingChannelAddress,
    MessagingChannelAddressFieldsWithDefaults
> {
    static readonly schemaKey = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    @validate({ zodValidator: z.enum(Object.values(MessagingChannel) as [string, ...string[]]) })
    @prop({ type: () => String, required: true, enum: Object.values(MessagingChannel) })
    public channel!: MessagingChannel;

    /**
     * The address with any channel prefix stripped. Every channel we accept is phone-backed, so E.164 is the whole
     * rule today. A field validator cannot see `channel`, so a channel addressed another way moves this rule off
     * the decorator. An addressing key, not an identity — handsets change hands.
     */
    @validate({ zodValidator: E164PhoneNumber })
    @prop({ type: () => String, required: true })
    public address!: string;

    static getters = {
        /** The endpoint as one key — see {@link channelScopedKey}. */
        getKey: (self: ChannelAddressKey): string => channelScopedKey(self.channel, self.address),
    } as const satisfies Record<string, (self: MessagingChannelAddress) => any>;

    static utilities = {} as const satisfies Record<string, (...args: any) => any>;

    static dtoValidators: DtoValidatorTypes<MessagingChannelAddressSchemaTypes>;

    constructor(
        properties?: SchemaConstructorArgs<MessagingChannelAddress, MessagingChannelAddressFieldsWithDefaults>,
    ) {
        super(properties);
        Object.assign(this, properties);
    }
}

MessagingChannelAddress.dtoValidators = getZodSchemaFromClass(
    MessagingChannelAddress,
) as unknown as DtoValidatorTypes<MessagingChannelAddressSchemaTypes>;

/**
 * A channel address a request or job payload may carry: the class's own validator, with the channel held
 * to {@link SUPPORTED_MESSAGING_CHANNELS}. Every inbound boundary parses through this. A refine, not a narrower
 * `channel` field: narrowing changes the parsed type and every caller that builds the class stops compiling.
 */
export function supportedChannelAddress() {
    return asZodObject(MessagingChannelAddress.dtoValidators.createLax).refine(
        (value) => (SUPPORTED_MESSAGING_CHANNELS as readonly string[]).includes(value.channel),
        { message: "unsupported messaging channel", path: ["channel"] },
    );
}
